import React, { useState } from 'react';
import {
  Typography, IconButton, Badge, Avatar,
  Menu, MenuItem, Box, Divider, Chip,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings as SettingsIcon,
  Logout,
  KeyboardArrowDown,
} from '@mui/icons-material';
import Logo from '../../../assets/Logo.png';

interface HeaderProps {
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
  user?: { name: string; email: string; avatar?: string; role: string };
  notifications?: {
    count: number;
    items: Array<{ id: string; title: string; message: string; time: string; severity: 'info' | 'warning' | 'error' }>;
  };
  onLogout?: () => void;
  contentOffset?: number;
}

const SEVERITY_COLOR: Record<string, string> = {
  error: '#EF4444', warning: '#F59E0B', info: '#3B82F6',
};

const ROLE_COLOR: Record<string, { bg: string; text: string }> = {
  Admin:    { bg: '#EFF6FF', text: '#1D4ED8' },
  Manager:  { bg: '#F0FDF4', text: '#15803D' },
  Operator: { bg: '#FFF7ED', text: '#C2410C' },
  Auditor:  { bg: '#F5F3FF', text: '#6D28D9' },
};

const iconBtnSx = {
  width: 38, height: 38, borderRadius: '9px',
  bgcolor: '#F9FAFB', border: '1px solid #E5E7EB',
  color: '#6B7280',
  '&:hover': { bgcolor: '#F3F4F6' },
};

export const Header: React.FC<HeaderProps> = ({
  onMenuToggle,
  showMenuButton = true,
  user = { name: 'John Doe', email: 'john@example.com', role: 'Operator' },
  notifications = { count: 0, items: [] },
  onLogout,
  contentOffset = 0,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notifAnchor,   setNotifAnchor]   = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const offset    = isMobile ? 0 : contentOffset;
  const initials  = user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const roleStyle = ROLE_COLOR[user.role] ?? { bg: '#F1F5F9', text: '#475569' };

  return (
    <>
      {/* ── Header bar ── */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: offset,
        width: `calc(100% - ${offset}px)`,
        height: isMobile ? 56 : 64,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #E5E7EB',
        boxShadow: '0 1px 0 #E5E7EB, 0 4px 16px rgba(15,23,42,0.06)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingLeft: isMobile ? 12 : 20,
        paddingRight: isMobile ? 12 : 20,
        boxSizing: 'border-box',
        transition: 'left 0.25s ease, width 0.25s ease',
      }}>

        {/* Mobile: hamburger + logo */}
        {isMobile && showMenuButton && (
          <IconButton onClick={onMenuToggle} sx={{ color: '#374151' }}>
            <MenuIcon />
          </IconButton>
        )}
        {isMobile && (
          <Box component="img" src={Logo} alt="PetroTag360" sx={{ height: 30, objectFit: 'contain' }} />
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bell — right side */}
        <IconButton onClick={e => setNotifAnchor(e.currentTarget)} sx={iconBtnSx}>
          <Badge
            variant="dot"
            invisible={notifications.count === 0}
            sx={{ '& .MuiBadge-dot': { bgcolor: '#F59E0B', width: 8, height: 8, border: '2px solid #fff' } }}
          >
            <NotificationsIcon sx={{ fontSize: 19 }} />
          </Badge>
        </IconButton>

        {/* Divider */}
        <div style={{ width: 1, height: 26, backgroundColor: '#E5E7EB', flexShrink: 0 }} />

        {/* User */}
        <div
          onClick={e => setProfileAnchor(e.currentTarget)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 9, cursor: 'pointer' }}
        >
          {!isMobile && (
            <span style={{ fontSize: '0.855rem', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
              {user.name}
            </span>
          )}
          <Avatar
            src={user.avatar}
            sx={{ width: 34, height: 34, bgcolor: 'rgba(43,103,176,1)', fontSize: '0.75rem', fontWeight: 700, border: '2px solid #E5E7EB' }}
          >
            {!user.avatar ? initials : undefined}
          </Avatar>
          <KeyboardArrowDown sx={{ fontSize: 16, color: '#9CA3AF' }} />
        </div>

      </div>

      {/* ── Notifications dropdown ── */}
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{ sx: { width: 340, mt: 1, borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB', overflow: 'hidden' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>Notifications</Typography>
          {notifications.count > 0 && (
            <Chip label={`${notifications.count} new`} size="small"
              sx={{ bgcolor: '#FEF2F2', color: '#EF4444', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
          )}
        </Box>

        {notifications.items.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 32, color: '#D1D5DB', mb: 1 }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>No new notifications</Typography>
          </Box>
        ) : notifications.items.map(n => (
          <MenuItem key={n.id} onClick={() => setNotifAnchor(null)}
            sx={{ px: 2, py: 1.5, alignItems: 'flex-start', gap: 1.5, '&:hover': { bgcolor: '#F9FAFB' } }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, mt: 0.75, bgcolor: SEVERITY_COLOR[n.severity] ?? '#6B7280' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }} noWrap>{n.title}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#9CA3AF', whiteSpace: 'nowrap', ml: 1 }}>{n.time}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }} noWrap>{n.message}</Typography>
            </Box>
          </MenuItem>
        ))}

        {notifications.items.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #F3F4F6', textAlign: 'center' }}>
            <Typography onClick={() => setNotifAnchor(null)}
              sx={{ fontSize: '0.8rem', color: 'rgba(43,103,176,1)', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              View all notifications
            </Typography>
          </Box>
        )}
      </Menu>

      {/* ── Profile dropdown ── */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        PaperProps={{ sx: { width: 256, mt: 1, borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB', overflow: 'hidden' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #F3F4F6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(43,103,176,1)', fontWeight: 700, fontSize: '0.9rem' }}>
              {initials}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827' }}>{user.name}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }} noWrap>{user.email}</Typography>
            </Box>
          </Box>
          <Chip label={user.role} size="small"
            sx={{ bgcolor: roleStyle.bg, color: roleStyle.text, fontWeight: 600, fontSize: '0.7rem', height: 20, border: `1px solid ${roleStyle.text}30` }} />
        </Box>

        <MenuItem onClick={() => setProfileAnchor(null)}
          sx={{ px: 2, py: 1.25, gap: 1.5, fontSize: '0.85rem', color: '#374151', '&:hover': { bgcolor: '#F9FAFB' } }}>
          <AccountCircle sx={{ fontSize: 18, color: '#6B7280' }} /> Profile
        </MenuItem>
        <MenuItem onClick={() => setProfileAnchor(null)}
          sx={{ px: 2, py: 1.25, gap: 1.5, fontSize: '0.85rem', color: '#374151', '&:hover': { bgcolor: '#F9FAFB' } }}>
          <SettingsIcon sx={{ fontSize: 18, color: '#6B7280' }} /> Settings
        </MenuItem>
        <Divider sx={{ my: 0.5, borderColor: '#F3F4F6' }} />
        <MenuItem onClick={() => { setProfileAnchor(null); onLogout?.(); }}
          sx={{ px: 2, py: 1.25, gap: 1.5, fontSize: '0.85rem', color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
          <Logout sx={{ fontSize: 18 }} /> Sign Out
        </MenuItem>
      </Menu>
    </>
  );
};

export default Header;
